import {
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconLink,
  IconSend,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

const API_URL = import.meta.env.VITE_API_URL || "";

export const Route = createFileRoute("/j/$hash" as any)({
  component: PublicSubmissionPage,
});

interface LinkInfo {
  link: {
    id: string;
    name: string;
    description?: string;
    color?: string;
    defaultCategory?: string;
    projectName?: string;
  };
}

interface SubmissionResult {
  success: boolean;
  referenceNumber?: string;
  message: string;
}

function PublicSubmissionPage() {
  const params = Route.useParams();
  const hash = "hash" in params ? params.hash : "";
  const [submitted, setSubmitted] = useState<SubmissionResult | null>(null);

  // Fetch link info
  const { data, isLoading, error } = useQuery<LinkInfo>({
    queryKey: ["submission-link", hash],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/j/${hash}`);
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Link not found");
      }
      return response.json();
    },
    enabled: !!hash,
  });

  const link = data?.link;

  const form = useForm({
    initialValues: {
      submitterName: "",
      submitterPhone: "",
      submitterEmail: "",
      content: "",
      category: link?.defaultCategory || "",
    },
    validate: {
      submitterName: (value) => (value.length < 1 ? "Your name is required" : null),
      content: (value) => (value.length < 1 ? "Please enter your message" : null),
    },
  });

  // Update category when link data loads
  if (link?.defaultCategory && !form.values.category) {
    form.setFieldValue("category", link.defaultCategory);
  }

  const submitMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const response = await fetch(`${API_URL}/api/j/${hash}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Submission failed");
      }
      return response.json() as Promise<SubmissionResult>;
    },
    onSuccess: (result) => {
      setSubmitted(result);
      notifications.show({
        title: "Submitted!",
        message: result.message,
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message,
        color: "red",
      });
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    submitMutation.mutate(values);
  });

  // Loading state
  if (isLoading) {
    return (
      <Container size="sm" py="xl">
        <Center h={300}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Error state
  if (error || !link) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={64} color="red" variant="light">
              <IconLink size={32} />
            </ThemeIcon>
            <Title order={2}>Link Not Found</Title>
            <Text c="dimmed" ta="center">
              {error instanceof Error
                ? error.message
                : "This submission link is invalid, disabled, or has expired."}
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (submitted) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Stack align="center" gap="lg">
            <ThemeIcon size={80} color="green" variant="light" radius="xl">
              <IconCheck size={40} />
            </ThemeIcon>
            <Title order={2} ta="center">
              Submitted Successfully!
            </Title>
            {submitted.referenceNumber && (
              <Paper p="md" withBorder bg="gray.0">
                <Text ta="center" size="sm" c="dimmed">
                  Reference Number
                </Text>
                <Text ta="center" size="xl" fw={700}>
                  {submitted.referenceNumber}
                </Text>
              </Paper>
            )}
            <Text c="dimmed" ta="center" size="lg">
              {submitted.message}
            </Text>
            <Button
              variant="light"
              onClick={() => {
                setSubmitted(null);
                form.reset();
              }}
            >
              Submit Another
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Submission form
  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Paper
          p="xl"
          withBorder
          style={{
            borderTop: `4px solid ${link.color || "#be4bdb"}`,
          }}
        >
          <Stack gap="md">
            <Group gap="sm">
              <ThemeIcon
                size={48}
                color={link.color || "grape"}
                variant="light"
                radius="xl"
              >
                <IconLink size={24} />
              </ThemeIcon>
              <div>
                <Title order={2}>{link.name}</Title>
                {link.projectName && (
                  <Text size="sm" c="dimmed">
                    {link.projectName}
                  </Text>
                )}
              </div>
            </Group>

            {link.description && (
              <Text c="dimmed">{link.description}</Text>
            )}
          </Stack>
        </Paper>

        {/* Form */}
        <Paper p="xl" withBorder>
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Your Name"
                placeholder="Enter your name"
                {...form.getInputProps("submitterName")}
                required
              />

              <Group grow>
                <TextInput
                  label="Phone"
                  placeholder="Optional"
                  {...form.getInputProps("submitterPhone")}
                />
                <TextInput
                  label="Email"
                  placeholder="Optional"
                  type="email"
                  {...form.getInputProps("submitterEmail")}
                />
              </Group>

              <Textarea
                label="Message"
                placeholder="What would you like to share?"
                minRows={4}
                {...form.getInputProps("content")}
                required
              />

              <Select
                label="Category"
                placeholder="Select category"
                clearable
                data={[
                  { value: "checkpoint", label: "Checkpoint / Progress" },
                  { value: "issue", label: "Issue / Problem" },
                  { value: "delivery", label: "Delivery" },
                  { value: "receipt", label: "Receipt" },
                  { value: "update", label: "Update" },
                  { value: "general", label: "General" },
                ]}
                {...form.getInputProps("category")}
              />

              <Button
                type="submit"
                size="lg"
                fullWidth
                leftSection={<IconSend size={18} />}
                loading={submitMutation.isPending}
                mt="md"
              >
                Submit
              </Button>
            </Stack>
          </form>
        </Paper>

        <Text size="xs" c="dimmed" ta="center">
          Your submission will be added to the project journal.
        </Text>
      </Stack>
    </Container>
  );
}
